/* global APP_THEME */
import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useStorage, type RemovableRef } from '@vueuse/core';
import uniqid from 'uniqid';
import { cloneDeep, merge, omit } from 'lodash-es';
import { ComputeProvisioningEligibility, TaskState } from '@schema';
import type {
  BackendTask,
  ComputeProvisioningAccepted,
  ComputeProvisioningCancelRequest,
  ComputeProvisioningPhase,
  ComputeProvisioningProblem,
  ComputeProvisioningRequest,
  ComputeProvisioningRetryContext,
  ComputeProvisioningRetryRequest,
  ComputeProvisioningStoragePolicies,
  MultipathConfig,
  ScheduledTask,
  NetworkInterfaceValidationRequest,
  NetworkInterfaceValidationResult,
  StoragePolicyIntent,
  StorageProvisioningNodeIntent
} from '@schema';
import mapPhases from '@/common/store/pinia/computeProvisioning/computeProvisioningMapper';
import { NETWORK_PHYSICAL_TYPES } from '@/common/constants/networks.constants';
import hciThemeData from '@/hci/config/themes';
import type { ExternalStorageFormModel } from '@/common/composables/useExternalStorage';
import type { FinalizedStoragePlan } from '@/common/store/pinia/computeProvisioningStoragePlan';
import api from '@/common/utils/api';
import CommonError from '@/common/utils/errors/CommonError';
import type { NodeInterfaceEdits, NodeNetworkRolesets, RolesetTrafficRoles } from '@/hci/pages/infrastructure/nodes/createComputeClusterWizard/nodeNetworkMatrix';

export type ComputeAllocationPool = {
  startAddress: string;
  endAddress: string;
};

export type ComputeDnsServer = {
  value: string;
};

export type ComputeDhcpAndDnsModel = {
  dhcp: boolean;
  pools: ComputeAllocationPool[];
  dns: ComputeDnsServer[];
};

export type ComputeServicesModel = {
  maas: boolean;
  'infra-optim': boolean;
  k8saas: boolean;
  lbaas: boolean;
  metering: boolean;
};

export type ComputeClusterFormModel = {
  clusterName: string;
  nodeIds: ID[] | null;
  storageNodes: StorageProvisioningNodeIntent[] | null;
  vmHaPlanned: boolean;
  configureExternalStorage: boolean;
  externalStorage: ExternalStorageFormModel | null;
  externalStorageClientRef: string | null;
  multipathConfig: MultipathConfig | null;
  storagePolicies: ComputeProvisioningStoragePolicies | null;
  requestedStoragePolicies: Record<string, Pick<StoragePolicyIntent, 'redundancy' | 'failure_domain' | 'tier_selector'>>;
  nodeNetworkRolesets: NodeNetworkRolesets;
  nodeInterfaceEdits: NodeInterfaceEdits;
  rolesetTrafficRoles: RolesetTrafficRoles;
  trafficRolesCustomized: boolean;
  networkInterfaceWarnings: ComputeProvisioningProblem[];
  ipManagement: boolean;
  physicalNetwork: ID | null;
  physicalType: string;
  vlanId: number | null;
  cidr: string;
  gateway: string;
  dhcpAndDns: ComputeDhcpAndDnsModel;
  services: ComputeServicesModel;
};

export type ComputeClusterDefaults = {
  nodeIds?: ID[];
  physicalNetwork?: ID;
  subnet?: { cidr: string; gateway: string };
  storageNodes?: StorageProvisioningNodeIntent[];
  storagePolicies?: ComputeProvisioningStoragePolicies;
  finalizedStoragePlan?: FinalizedStoragePlan;
};

type ComputeClusterDraft = {
  version: number;
  step: string;
  completedStepNames: string[];
  defaults: ComputeClusterDefaults;
  model: ComputeClusterFormModel;
};

export type ComputeProvisioningStatus = Omit<BackendTask, 'state'> & {
  state: TaskState;
  revision_id: ID;
  phases: ComputeProvisioningPhase[];
  retry_context?: ComputeProvisioningRetryContext;
  problem?: ComputeProvisioningProblem;
  warnings: ComputeProvisioningProblem[];
  partial: boolean;
};

const COMPUTE_PROVISIONING_URL = '/api/v2/compute/provisioning';
const COMPUTE_PROVISIONING_CANCEL_URL = `${COMPUTE_PROVISIONING_URL}/cancel`;
const COMPUTE_PROVISIONING_RETRY_URL = `${COMPUTE_PROVISIONING_URL}/retry/`;
const COMPUTE_PROVISIONING_ELIGIBILITY_URL = `${COMPUTE_PROVISIONING_URL}/eligibility/`;
const NETWORK_INTERFACE_VALIDATIONS_URL = '/api/v2/network/interface/validations/';

export const DRAFT_STORAGE_KEY = 'computeProvisioning:draft';
export const PROVISIONING_STARTED_AT_STORAGE_KEY = 'computeProvisioning:startedAt';
export const PROVISIONING_OPERATION_ID_STORAGE_KEY = 'computeProvisioning:operationId';
export const PROVISIONING_REPORTED_OUTCOME_STORAGE_KEY = 'computeProvisioning:reportedOutcome';
export const PROVISIONING_STEP_TRAIL_STEP_STORAGE_KEY = 'computeProvisioning:stepTrailStep';
export const PROVISIONING_STEP_TRAIL_ENTERED_AT_STORAGE_KEY = 'computeProvisioning:stepTrailEnteredAt';

export const DRAFT_VERSION = 2;

const RESUMABLE_ELIGIBILITY_ACTIONS: ComputeProvisioningEligibility.action[] = [
  ComputeProvisioningEligibility.action.CONTINUE,
  ComputeProvisioningEligibility.action.RECOVER
];

export const TERMINAL_STATES = [
  TaskState.SUCCESS,
  TaskState.FAILED,
  TaskState.ABORTED,
  TaskState.CANCELLED
];

const FAILURE_STATES = [
  TaskState.FAILED
];

const CANCELLED_STATES = [
  TaskState.CANCELLED,
  TaskState.ABORTED
];

export const createInitialDraft = (): ComputeClusterDraft => ({
  version: DRAFT_VERSION,
  step: '',
  completedStepNames: [],
  defaults: {},
  model: {
    clusterName: '',
    nodeIds: null,
    storageNodes: null,
    vmHaPlanned: false,
    configureExternalStorage: false,
    externalStorage: null,
    externalStorageClientRef: null,
    multipathConfig: null,
    storagePolicies: null,
    requestedStoragePolicies: {},
    nodeNetworkRolesets: {},
    nodeInterfaceEdits: {},
    rolesetTrafficRoles: {},
    trafficRolesCustomized: false,
    networkInterfaceWarnings: [],
    ipManagement: hciThemeData[APP_THEME].computeIpManagementDefault,
    physicalNetwork: null,
    physicalType: NETWORK_PHYSICAL_TYPES.UNTAGGED,
    vlanId: null,
    cidr: '',
    gateway: '',
    dhcpAndDns: {
      dhcp: true,
      pools: [],
      dns: []
    },
    services: {
      maas: false,
      'infra-optim': false,
      k8saas: false,
      lbaas: false,
      metering: false
    }
  }
});

export const stripDraftSecrets = (draft: ComputeClusterDraft): ComputeClusterDraft => {
  const stripped = cloneDeep(draft);
  const { externalStorage } = stripped.model;
  if (!externalStorage) {
    return stripped;
  }
  externalStorage.secret_params = {};
  if (externalStorage.volume_driver_secret_params) {
    externalStorage.volume_driver_secret_params = externalStorage.volume_driver_secret_params
      .map(param => ({ ...param, value: '' }));
  }
  return stripped;
};

const isTerminal = (state: TaskState) => TERMINAL_STATES.includes(state);

type PersistedDraft = Partial<Omit<ComputeClusterDraft, 'model'>> & { model?: Partial<ComputeClusterFormModel> };

// Treats the staged traffic types of a draft persisted before the choice was tracked as chosen.
const restoreDraft = (persisted: PersistedDraft, defaults: ComputeClusterDraft): ComputeClusterDraft => {
  const draft: ComputeClusterDraft = merge({}, defaults, persisted);
  if (persisted.model?.trafficRolesCustomized === undefined) {
    draft.model.trafficRolesCustomized = Object.keys(persisted.model?.rolesetTrafficRoles || {}).length > 0;
  }
  return draft;
};

export const useComputeProvisioningStore = defineStore('computeProvisioning', () => {
  const task = ref<ComputeProvisioningStatus | null>(null);

  const submitting = ref(false);

  const currTaskId = ref<ID | null>(null);

  const draft = useStorage(DRAFT_STORAGE_KEY, createInitialDraft(), localStorage, {
    mergeDefaults: (storageValue: PersistedDraft, defaults) => (storageValue?.version === defaults.version
      ? restoreDraft(storageValue, defaults)
      : defaults),
    writeDefaults: false,
    listenToStorageChanges: false,
    serializer: {
      read: JSON.parse,
      write: value => JSON.stringify(stripDraftSecrets(value))
    }
  });

  const eligibility = ref<ComputeProvisioningEligibility | null>(null);

  const provisioningStorageOptions = { listenToStorageChanges: false, flush: 'sync' as const, onError: () => {} };
  const operationId: RemovableRef<string | null> = useStorage(
    PROVISIONING_OPERATION_ID_STORAGE_KEY, null, localStorage, provisioningStorageOptions
  );
  const startedAt: RemovableRef<number> = useStorage(
    PROVISIONING_STARTED_AT_STORAGE_KEY, 0, localStorage, provisioningStorageOptions
  );
  const reportedOutcome: RemovableRef<string | null> = useStorage(
    PROVISIONING_REPORTED_OUTCOME_STORAGE_KEY, null, localStorage, provisioningStorageOptions
  );
  const stepTrailStep: RemovableRef<string | null> = useStorage(
    PROVISIONING_STEP_TRAIL_STEP_STORAGE_KEY, null, localStorage, provisioningStorageOptions
  );
  const stepTrailEnteredAt: RemovableRef<number> = useStorage(
    PROVISIONING_STEP_TRAIL_ENTERED_AT_STORAGE_KEY, 0, localStorage, provisioningStorageOptions
  );

  const ensureProvisioningOperationId = (): string => {
    if (!operationId.value) {
      operationId.value = uniqid('op-');
    }
    return operationId.value;
  };

  type StepEnteredResult = { from: string | null; secondsOnPrevStep: number | null };

  const recordStepEntered = (stepName: string): StepEnteredResult | null => {
    if (!stepName || stepName === stepTrailStep.value) {
      return null;
    }
    const from = stepTrailStep.value;
    const now = Date.now();
    const secondsOnPrevStep = stepTrailEnteredAt.value
      ? Math.round((now - stepTrailEnteredAt.value) / 1000)
      : null;
    if (from === null) {
      startedAt.value = now;
      operationId.value = uniqid('op-');
      reportedOutcome.value = null;
    }
    stepTrailStep.value = stepName;
    stepTrailEnteredAt.value = now;
    return { from, secondsOnPrevStep };
  };

  const clearProvisioningTimer = (): void => {
    startedAt.value = 0;
    operationId.value = null;
    stepTrailStep.value = null;
    stepTrailEnteredAt.value = 0;
  };

  const clearDraft = () => {
    draft.value = createInitialDraft();
  };

  const hasDraftInProgress = computed(() => !!draft.value.step);

  const phases = computed(() => mapPhases(task.value));

  const isActive = computed(() => !!task.value && !isTerminal(task.value.state));

  const isFailed = computed(() => !!task.value && FAILURE_STATES.includes(task.value.state));

  const retryContext = computed(() => task.value?.retry_context);

  const canRetry = computed(() => isFailed.value && !!retryContext.value);

  const isSuccess = computed(() => !!task.value && task.value.state === TaskState.SUCCESS);

  const isCancelled = computed(() => !!task.value && CANCELLED_STATES.includes(task.value.state));

  const isProvisioning = computed(() => {
    if (submitting.value) {
      return true;
    }
    if (task.value) {
      return !isTerminal(task.value.state);
    }
    return !!currTaskId.value;
  });

  const fetchTask = (taskId?: ID): Promise<ComputeProvisioningStatus | null> => api
    .get<ComputeProvisioningStatus>(COMPUTE_PROVISIONING_URL,
      taskId ? { task_id: taskId } : {}
    )
    .then(({ data }) => {
      if (!data) {
        task.value = null;
        currTaskId.value = null;
        return task.value;
      }
      task.value = data;
      currTaskId.value = data.task_id;
      return task.value;
    });

  const createTask = (payload: ComputeProvisioningRequest) => {
    task.value = null;
    currTaskId.value = null;
    submitting.value = true;
    return api
      .post<ScheduledTask>(COMPUTE_PROVISIONING_URL, payload)
      .then(({ data }) => {
        currTaskId.value = data.task_id;
        // the first task event may arrive before the POST response and be skipped by onTasks
        return fetchTask(data.task_id)
          .catch(() => null)
          .then(() => data);
      })
      .finally(() => {
        submitting.value = false;
      });
  };

  const retryTask = (request: ComputeProvisioningRequest) => {
    if (!task.value?.task_id || !task.value?.revision_id) {
      return Promise.reject(new CommonError('No failed task to retry'));
    }
    const payload: ComputeProvisioningRetryRequest = {
      expected_task_id: task.value.task_id,
      expected_revision_id: task.value.revision_id,
      request
    };
    submitting.value = true;
    return api
      .post<ComputeProvisioningAccepted>(COMPUTE_PROVISIONING_RETRY_URL, payload)
      .then(({ data }) => {
        currTaskId.value = data.task_id;
        return fetchTask(data.task_id);
      })
      .finally(() => {
        submitting.value = false;
      });
  };

  const validateNetworkInterfaces = (
    request: NetworkInterfaceValidationRequest
  ): Promise<NetworkInterfaceValidationResult> => api
    .post<NetworkInterfaceValidationResult>(NETWORK_INTERFACE_VALIDATIONS_URL, request)
    .then(({ data }) => data);

  const cancelTask = () => {
    if (!task.value?.task_id) {
      return Promise.resolve(null);
    }
    const payload: ComputeProvisioningCancelRequest = {
      expected_task_id: task.value.task_id
    };
    return api
      .post<ComputeProvisioningStatus>(COMPUTE_PROVISIONING_CANCEL_URL, payload)
      .then(({ data }) => {
        task.value = data;
        return data;
      });
  };

  const onTasks = (event: BackendTask) => {
    if (!currTaskId.value || event.task_id !== currTaskId.value) {
      return;
    }
    fetchTask(event.task_id).catch(() => {});
  };

  const fetchEligibility = (): Promise<ComputeProvisioningEligibility> => api
    .get<ComputeProvisioningEligibility>(COMPUTE_PROVISIONING_ELIGIBILITY_URL)
    .then(({ data }) => {
      eligibility.value = data;
      return data;
    });

  watch(isCancelled, (cancelled) => {
    if (cancelled) {
      clearDraft();
    }
  });

  watch(retryContext, (context) => {
    const requiredNames = (context?.required_secret_inputs || []).flatMap(input => input.parameter_names);
    const { externalStorage } = draft.value.model;
    if (!requiredNames.length || !externalStorage?.secret_params) {
      return;
    }
    externalStorage.secret_params = omit(externalStorage.secret_params, requiredNames);
  });

  const canResume = computed(() => (
    RESUMABLE_ELIGIBILITY_ACTIONS.includes(eligibility.value?.action as ComputeProvisioningEligibility.action)
    || hasDraftInProgress.value
  ));

  return {
    task,
    submitting,
    draft,
    eligibility,
    operationId,
    startedAt,
    reportedOutcome,
    stepTrailStep,
    stepTrailEnteredAt,
    ensureProvisioningOperationId,
    recordStepEntered,
    clearProvisioningTimer,
    phases,
    isActive,
    isFailed,
    isSuccess,
    isCancelled,
    isProvisioning,
    hasDraftInProgress,
    canResume,
    retryContext,
    canRetry,
    clearDraft,
    fetchTask,
    fetchEligibility,
    validateNetworkInterfaces,
    createTask,
    retryTask,
    cancelTask,
    onTasks
  };
}, {
  ws: {
    tasks: 'onTasks'
  }
});

export default useComputeProvisioningStore;
